import { useLinkLogic } from '@/app/navigation/stack-links';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { EntityForm } from '@/components/forms/EntityForm';
import { departmentCharToTagValue } from '@/features/lists/components/departmentTag';
import { usePostListEventsMutation } from '@/features/lists/lists.hooks';
import { createChecklistLayout } from '@/features/lists/lists.schemas';
import { TemplatePrefilterContext } from '@/features/lists/components/TemplateSelectorField';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useWork } from '@/features/works/works.hooks';
import { useEntityForm } from '@/hooks/useEntityForm';
import { createListEvent, CreateListPayload, ListEvent } from '@certhon/models';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';
import { toast } from 'sonner';

export const ChecklistCreatePanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const makeLink = useLinkLogic();
  const werkId = parseInt(params.workId);

  const { current: list_id } = useRef(crypto.randomUUID());

  const { data: work } = useWork(werkId);
  const departmentTagValue = work?.afd
    ? departmentCharToTagValue(work.afd)
    : null;

  // Templates are pre-filtered to "composed" templates of the work's
  // department so users see the relevant set first. Falls back to just
  // `label:composed` when the department can't be resolved.
  const prefilterTags = useMemo(() => {
    const tags = ['label:composed'];
    if (departmentTagValue) tags.push(`department:${departmentTagValue}`);
    return tags;
  }, [departmentTagValue]);

  const defaultValues = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      name: urlParams.get('name') || '',
      template_id: urlParams.get('template_id') || null,
      associated_users: [],
      start_date: null,
      end_date: null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityForm = useEntityForm({
    layout: createChecklistLayout,
    isLoading: false,
    data: undefined,
    defaultValues,
  });

  const postListEventsMutation = usePostListEventsMutation();

  const handleCancel = () => {
    window.history.back();
    return false;
  };

  return (
    <Panel {...panelProps}>
      <TemplatePrefilterContext.Provider value={prefilterTags}>
        <EntityForm
          {...entityForm}
          onCancel={handleCancel}
          onSubmit={entityForm.form.handleSubmit(
            async (formValues) => {
              const payload: CreateListPayload = {
                name: formValues.name,
                werk_id: werkId,
                tags: ['qa'],
                associated_users: formValues.associated_users ?? [],
                start_date: formValues.start_date ?? undefined,
                end_date: formValues.end_date ?? undefined,
              };

              try {
                const eventsToPost: ListEvent[] = [];

                eventsToPost.push(
                  createListEvent('CREATE_LIST', {
                    user_id: user!.recnum,
                    list_id,
                    payload,
                  }),
                );

                // If a template was picked, expand it into the new list root.
                // The new list's root node id equals the list id (see reducer
                // `createRootField` usage and the seed script for reference).
                if (formValues.template_id) {
                  eventsToPost.push(
                    createListEvent('EXPAND_PROTO', {
                      user_id: user!.recnum,
                      list_id,
                      payload: {
                        targetNodeId: list_id,
                        protoListId: formValues.template_id,
                      },
                    }),
                  );
                }

                await postListEventsMutation.mutateAsync(eventsToPost);

                // Navigate to the new list
                const to = makeLink(path.replace(/new$/, list_id));
                navigate({ to });
              } catch (error) {
                toast.error('Could not create list', {
                  description:
                    error instanceof Error ? error.message : 'Unknown error',
                });
              }
            },
            (errors) => {
              const first = Object.values(errors)[0]?.message;
              toast.error('Form validation failed', {
                description: first ?? 'Please check the form fields.',
              });
            },
          )}
        />
      </TemplatePrefilterContext.Provider>
    </Panel>
  );
};
